import { useEffect, useState } from "react";
import { currency, type CurrencyMeta, type CurrencyOf, type NumberFormat } from "../../api/currency";
import { fmtInr } from "../format";

/*
 * Money in a document's own currency, for display only. A row or document the server sends in another currency carries
 * currency_code, currency_symbol, currency_decimals and currency_format; one without them is INR and goes to fmtInr as before.
 */

const GROUPS: Record<NumberFormat, { group: string; point: string; indian: boolean }> = {
  comma_dot: { group: ",", point: ".", indian: false },
  indian: { group: ",", point: ".", indian: true },
  dot_comma: { group: ".", point: ",", indian: false },
  space_comma: { group: " ", point: ",", indian: false },
  plain: { group: "", point: ".", indian: false },
};

/** "1234.5" in USD -> "$1,234.50"; in INR (or with no currency) -> fmtInr. */
export function fmtMoney(value: string | null | undefined, c?: CurrencyOf | null, exact = true): string {
  if (!c?.currency_code || c.currency_code === "INR" || !c.currency_symbol) return fmtInr(value, exact);
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const d = c.currency_decimals ?? 2;
  const f = GROUPS[c.currency_format ?? "comma_dot"] ?? GROUPS.comma_dot;
  const [int, frac] = Math.abs(n).toFixed(d).split(".");
  let head = int;
  if (f.group && f.indian && int.length > 3) head = `${int.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, f.group)}${f.group}${int.slice(-3)}`;
  else if (f.group) head = int.replace(/\B(?=(\d{3})+(?!\d))/g, f.group);
  const sym = c.currency_symbol.length > 1 && /^[A-Z]+$/.test(c.currency_symbol) ? `${c.currency_symbol} ` : c.currency_symbol;
  return `${n < 0 ? "−" : ""}${sym}${head}${frac ? f.point + frac : ""}`;
}

/** The currency fields for a code, from the meta (for a form that only knows the code). */
export function currencyOf(meta: CurrencyMeta | null | undefined, code: string | null | undefined): CurrencyOf | null {
  const c = code ? meta?.currencies.find((x) => x.code === code) : null;
  return c ? { currency_code: c.code, currency_symbol: c.symbol, currency_decimals: c.decimals, currency_format: c.format } : null;
}

/** Is this row in another currency? */
export const isForeign = (c?: CurrencyOf | null) => Boolean(c?.currency_code && c.currency_code !== "INR");

/** "₹83.45 per USD" for a rate shown beside an amount. */
export const rateText = (c?: CurrencyOf | null) => (isForeign(c) && c?.exchange_rate ? `1 ${c.currency_code} = ₹${c.exchange_rate}` : "");

let cached: Promise<CurrencyMeta> | null = null;
let cachedAt = 0;

/** The currency meta (currencies, treatments, each party's currency), shared by every form and kept for a minute. */
export function useCurrencyMeta(): CurrencyMeta | null {
  const [meta, setMeta] = useState<CurrencyMeta | null>(null);
  useEffect(() => {
    let live = true;
    if (!cached || Date.now() - cachedAt > 60_000) {
      cachedAt = Date.now();
      cached = currency.meta();
      cached.catch(() => (cached = null));
    }
    cached.then((m) => live && setMeta(m)).catch(() => live && setMeta(null));
    return () => {
      live = false;
    };
  }, []);
  return meta;
}

/** After a currency, a rate or a party changes, forms load the meta again. */
export const forgetCurrencyMeta = () => {
  cached = null;
};
